export const formatReportDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

export const formatReportDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${formatReportDate(value)}, ${date.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

const parseReportDateTime = (value, dateOnlyAtEnd = false) => {
  const match = String(value || '').match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[, ]+(\d{2}):(\d{2}))?$/);
  if (!match) return null;
  const [, day, month, year, hour, minute] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day),
    hour ? Number(hour) : dateOnlyAtEnd ? 23 : 0,
    minute ? Number(minute) : dateOnlyAtEnd ? 59 : 0,
    dateOnlyAtEnd && !hour ? 59 : 0);
  if (date.getFullYear() !== Number(year) || date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) return null;
  return date;
};

export const getDeadlineClosureSummary = (rows = []) => rows.reduce((summary, row) => {
  if (String(row.cardStatus || '').trim() !== 'Tayyor') return summary;
  const deadline = parseReportDateTime(row.deadline, true);
  const completedAt = parseReportDateTime(row.statusChangedAt);
  if (!deadline || !completedAt) return summary;
  if (completedAt <= deadline) summary.onTime += 1;
  else summary.late += 1;
  return summary;
}, { onTime: 0, late: 0 });

export const normalizeReportTasks = (tasks = {}) => {
  const normalized = {};
  Object.entries(tasks).forEach(([boardId, boardTasks]) => {
    if (!boardTasks || typeof boardTasks !== 'object') {
      normalized[boardId] = [];
      return;
    }

    const rows = [];
    Object.entries(boardTasks).forEach(([day, cards]) => {
      if (day === '_order' || !Array.isArray(cards)) return;
      cards.forEach((card) => {
        if (!card || typeof card !== 'object') return;
        rows.push({
          id: card.id || `${boardId}-${day}-${rows.length}`,
          boardId,
          day,
          title: card.text || 'Nomsiz vazifa',
          status: card.status || 'Ochiq',
          assigned: Array.isArray(card.assigned) ? card.assigned : [],
          deadline: card.deadline || null,
          description: card.description || '',
          history: Array.isArray(card.history) ? card.history : [],
          statusHistory: Array.isArray(card.statusHistory) ? card.statusHistory : [],
          createdAt: card.createdAt || card.openedAt || null,
          openedAt: card.openedAt || card.createdAt || null,
          createdBy: card.createdBy || null,
          updatedAt: card.updatedAt || null,
          updatedBy: card.updatedBy || null,
        });
      });
    });
    normalized[boardId] = rows;
  });
  return normalized;
};

export const buildReportRows = (tasks, boardId, dateValue, members = {}, startDateValue = null, endDateValue = null) => {
  const normalized = normalizeReportTasks(tasks);
  const selectedDate = dateValue ? new Date(`${dateValue}T00:00:00`) : null;
  const startDate = startDateValue ? new Date(`${startDateValue}T00:00:00`) : selectedDate;
  const endDate = endDateValue ? new Date(`${endDateValue}T23:59:59`) : selectedDate;

  const isWithinDateRange = (value) => {
    if (!value || Number.isNaN(value.getTime())) return false;
    if (startDate && value < startDate) return false;
    if (endDate && value > endDate) return false;
    return true;
  };

  const boardCards = boardId === 'all'
    ? Object.values(normalized).flat()
    : normalized[boardId] || [];

  const rows = boardCards.filter((card) => {
    if (!selectedDate && !startDate && !endDate) return true;

    const matchValues = [];
    if (card.deadline?.date) {
      const [day, month, year] = card.deadline.date.split('.').map(Number);
      const deadlineDate = new Date(year, month - 1, day, 0, 0, 0);
      matchValues.push(deadlineDate);
    }
    if (card.createdAt || card.openedAt) matchValues.push(new Date(card.createdAt || card.openedAt));
    if (card.updatedAt) matchValues.push(new Date(card.updatedAt));
    (card.statusHistory || []).forEach((event) => {
      if (event?.date) matchValues.push(new Date(event.date));
    });
    (card.history || []).forEach((event) => {
      if (event?.date) matchValues.push(new Date(event.date));
    });

    return matchValues.some((value) => !Number.isNaN(value.getTime()) && isWithinDateRange(value));
  });

  return rows.map((card) => {
    const assigneeIds = card.assigned || [];
    const assigneeNames = assigneeIds
      .map((id) => members[id]?.name || members[id]?.email || id)
      .filter(Boolean)
      .join(', ') || '—';

    const createdAtValue = card.createdAt || card.openedAt || null;
    const createdAt = createdAtValue ? new Date(createdAtValue) : null;
    const updatedAt = card.updatedAt ? new Date(card.updatedAt) : null;
    const latestEvent = card.history?.[0];
    const statusHistory = (card.statusHistory || []).map((event) => {
      const date = new Date(event?.date);
      const statusDate = Number.isNaN(date.getTime()) ? '—' : formatReportDateTime(event.date);
      const authorValue = typeof event?.author === 'string' ? event.author : (typeof event?.authorName === 'string' ? event.authorName : '—');
      const author = authorValue === '—' ? '—' : (members[authorValue]?.name || members[authorValue]?.email || authorValue);
      return {
        status: event?.status || '—',
        date: statusDate,
        author,
      };
    });
    const currentStatusEvent = statusHistory.find((event) => event.status === card.status) || null;

    return {
      id: card.id,
      boardId,
      day: card.day,
      title: card.title,
      creator: typeof card.createdBy === 'string'
        ? (members[card.createdBy]?.name || members[card.createdBy]?.email || card.createdBy)
        : '—',
      createdAt: createdAt && !Number.isNaN(createdAt.getTime()) ? formatReportDateTime(createdAt.toISOString()) : '—',
      cardStatus: card.status,
      statusChangedAt: currentStatusEvent?.date || '—',
      statusChangedBy: currentStatusEvent?.author || '—',
      assignee: assigneeNames,
      deadline: card.deadline?.date && card.deadline?.time
        ? `${card.deadline.date} ${card.deadline.time}`
        : card.deadline?.date || '—',
      updatedAt: updatedAt && !Number.isNaN(updatedAt.getTime()) ? formatReportDateTime(updatedAt.toISOString()) : '—',
      updatedBy: typeof card.updatedBy === 'string'
        ? (members[card.updatedBy]?.name || members[card.updatedBy]?.email || card.updatedBy)
        : '—',
      activity: latestEvent?.text || '—',
      activityDate: latestEvent?.date || '—',
      description: card.description || '—',
      statusHistory,
    };
  });
};

export const groupReportRowsByBoard = (rows = []) => {
  return rows.reduce((groups, row) => {
    if (!row?.boardId) return groups;
    const boardName = row.boardId || 'Boshqa';
    groups[boardName] = groups[boardName] || [];
    groups[boardName].push(row);
    return groups;
  }, {});
};

export const selectReportRows = (rows = [], selectedIds = []) => {
  const ids = new Set(selectedIds.filter(Boolean));
  return rows.filter((row) => ids.has(row.id)).map((row) => ({ ...row }));
};
